import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, ChevronRight, FileText, Wrench, PackageSearch, Clock, ArrowRight, Plus } from 'lucide-react';
import { useWorkshop, type Job } from '../../modules/mechanic/hooks/useWorkshop';

interface Props {
    onNewJob: () => void;
}

type Bucket = 'ready_to_invoice' | 'awaiting_payment' | 'in_progress' | 'waiting_parts' | 'scheduled';

// Action-button colour is driven by the job's status, not the button label:
// "ready to invoice" is a positive, money-in action (green); "awaiting payment"
// is a chase-this reminder (amber/orange, the current default).
type ActionVariant = 'success' | 'warning';

interface BucketMeta {
    key: Bucket;
    label: string;
    icon: React.ElementType;
    accent: boolean; // amber-highlighted (money action)
    actionLabel?: string; // shown on the row's action button
    variant?: ActionVariant; // status → colour for that button
}

const BUCKET_ORDER: BucketMeta[] = [
    { key: 'ready_to_invoice', label: 'Ready to invoice', icon: FileText, accent: true, actionLabel: 'Invoice', variant: 'success' },
    { key: 'awaiting_payment', label: 'Awaiting payment', icon: Clock, accent: true, actionLabel: 'Open', variant: 'warning' },
    { key: 'in_progress', label: 'In progress', icon: Wrench, accent: false },
    { key: 'waiting_parts', label: 'Waiting on parts', icon: PackageSearch, accent: false },
    { key: 'scheduled', label: 'Not started yet', icon: Clock, accent: false },
];

// One shared button footprint for every row action so "Invoice" and "Open"
// are identical in size — only the colour (from `variant`) differs.
// These are labels inside the row's own button (the whole row is the tap
// target), so they are styled like buttons but are not separate controls.
const ACTION_BUTTON_BASE =
    'shrink-0 inline-flex items-center justify-center gap-1 h-8 px-2.5 rounded-[var(--fd-r-sm)] border text-fd-sm-tight font-display font-semibold transition-colors';

const ACTION_VARIANT_CLASS: Record<ActionVariant, string> = {
    // Money in: brass, the money metal (fd-btn-brass). Its own border and
    // shadow come from the class.
    success: 'fd-btn-brass',
    // Chase: a secondary key — a reminder, not the screen's main act.
    warning: 'fd-btn-secondary',
};

/**
 * Job List / To-Do — the lead column of the dashboard.
 *
 * This is the "what do I do next" surface Janis asked to lead with. Jobs are
 * bucketed by the action they need, derived purely from existing fields (job
 * status + whether an invoice exists / is paid) — there is NO new stored
 * status. "Ready to invoice" = an active job that has line items but no invoice
 * yet, so the mechanic's next move is to bill it. This deliberately avoids the
 * manual "mark ready" step that previously collided with the Pay-on-Device/QR
 * flow.
 */
export const JobsToDoCard = ({ onNewJob }: Props) => {
    const navigate = useNavigate();
    const { jobs, loading } = useWorkshop();

    const buckets = useMemo(() => classify(jobs), [jobs]);
    const total = buckets.reduce((sum, b) => sum + b.jobs.length, 0);

    // Route to the specific record, not the jobs list. WorkshopHub reads the
    // `job_id` param (not `job`) to highlight/scroll to a card; `action=invoice`
    // opens the invoice generator for that job.
    const openJob = (job: Job) => navigate(`/workshop?job_id=${job.id}`);
    const openInvoice = (job: Job) => navigate(`/workshop?job_id=${job.id}&action=invoice`);

    return (
        <section className="fd-card flex flex-col h-full min-h-[22rem]" aria-labelledby="dash-jobs-title">
            <header className="fd-card-header">
                <h2 id="dash-jobs-title" className="fd-card-title">
                    <Wrench aria-hidden="true" />
                    <span className="truncate">Job list</span>
                    {!loading && <span className="fd-count" aria-label={`${total} open`}>{total}</span>}
                </h2>
                <button onClick={onNewJob} className="fd-btn fd-btn-steel fd-btn-sm shrink-0">
                    <Plus aria-hidden="true" />
                    New job
                </button>
            </header>

            {loading ? (
                <div className="flex-1 grid place-items-center">
                    <span className="text-xs text-fg-4 font-mono">—</span>
                </div>
            ) : total === 0 ? (
                <div className="flex-1 flex flex-col items-center justify-center text-center px-5 py-8 gap-3">
                    <CheckCircle2 className="w-8 h-8 text-success" strokeWidth={1.5} />
                    <p className="text-sm text-fg-2 max-w-xs">
                        All caught up — no open jobs. Start one when the next motor rolls in.
                    </p>
                    <button onClick={onNewJob} className="fd-btn fd-btn-steel">
                        <Wrench aria-hidden="true" />
                        New job
                    </button>
                </div>
            ) : (
                <div className="fd-card-body fd-dash-scroll flex-1">
                    {buckets.map((b) => (
                        <div key={b.meta.key}>
                            <p
                                className="fd-row-group"
                                style={b.meta.accent ? { color: 'var(--fd-gold)' } : undefined}
                            >
                                <b.meta.icon aria-hidden="true" />
                                {b.meta.label} · {b.jobs.length}
                            </p>
                            <ul className="flex flex-col">
                                {b.jobs.map((job) => (
                                    <JobRow
                                        key={job.id}
                                        job={job}
                                        actionLabel={b.meta.actionLabel}
                                        variant={b.meta.variant}
                                        onTap={() =>
                                            b.meta.key === 'ready_to_invoice'
                                                ? openInvoice(job)
                                                : openJob(job)
                                        }
                                    />
                                ))}
                            </ul>
                        </div>
                    ))}
                </div>
            )}
        </section>
    );
};

function classify(jobs: Job[]): Array<{ meta: BucketMeta; jobs: Job[] }> {
    const map: Record<Bucket, Job[]> = {
        ready_to_invoice: [],
        awaiting_payment: [],
        in_progress: [],
        waiting_parts: [],
        scheduled: [],
    };

    for (const job of jobs) {
        if (job.is_void) continue;
        // Fully settled work drops off the to-do list.
        if (job.invoice_status === 'paid' || job.locked) continue;

        if (job.invoice_id) {
            // Invoice exists but isn't paid → chase payment.
            map.awaiting_payment.push(job);
        } else if (job.items.length > 0) {
            // Has billable work logged, no invoice yet → next move is to bill it.
            map.ready_to_invoice.push(job);
        } else if (job.status === 'waiting_parts') {
            map.waiting_parts.push(job);
        } else if (job.status === 'in_progress') {
            map.in_progress.push(job);
        } else {
            map.scheduled.push(job);
        }
    }

    return BUCKET_ORDER.map((meta) => ({ meta, jobs: map[meta.key] })).filter((b) => b.jobs.length > 0);
}

/** Past its expected completion and still on the to-do list. */
const isOverdue = (job: Job) =>
    !!job.estimated_completion && new Date(job.estimated_completion) < new Date();

const formatCurrency = (n: number) =>
    new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 }).format(n);

/** On a phone the amount leads the second line, so the customer's name keeps
 *  the width beside the plate; from sm it has its own column. */
const PhoneAmount = ({ total }: { total: number }) =>
    total > 0 ? (
        <span className="sm:hidden font-mono font-semibold text-fg tabular-nums">
            {formatCurrency(total)} ·{' '}
        </span>
    ) : null;

const JobRow = ({
    job,
    actionLabel,
    variant,
    onTap,
}: {
    job: Job;
    actionLabel?: string;
    variant?: ActionVariant;
    onTap: () => void;
}) => (
    <li className="min-w-0">
        <button onClick={onTap} className="fd-row">
            {job.vehicle_vrm && job.vehicle_vrm !== 'NO VRM' && (
                <span className="fd-plate">{job.vehicle_vrm}</span>
            )}
            <div className="flex-1 min-w-0 flex flex-col gap-0.5">
                <span className="text-sm font-medium text-fg truncate">
                    {job.customer_name}
                </span>
                {/* The hold-up note replaces the vehicle line when there is one:
                    on a job that's running late, why it's late is the thing the
                    owner is looking for. */}
                {job.status_note ? (
                    <span className="text-fd-sm-tight text-warning truncate">
                        <PhoneAmount total={job.total} />
                        {isOverdue(job) && '⏱ '}{job.status_note}
                    </span>
                ) : (
                    <span className="text-fd-sm-tight text-fg-3 truncate">
                        <PhoneAmount total={job.total} />
                        {isOverdue(job) && (
                            <span className="text-warning font-semibold">Overdue · </span>
                        )}
                        {job.vehicle_desc}
                    </span>
                )}
            </div>
            {job.total > 0 && (
                <span className="hidden sm:inline font-mono text-fd-sm-tight font-semibold text-fg tabular-nums flex-shrink-0">
                    {formatCurrency(job.total)}
                </span>
            )}
            {actionLabel && variant ? (
                <span className={`${ACTION_BUTTON_BASE} ${ACTION_VARIANT_CLASS[variant]}`}>
                    {actionLabel}
                    <ArrowRight className="w-3 h-3" />
                </span>
            ) : (
                <ChevronRight className="w-4 h-4 text-fg-4 flex-shrink-0" />
            )}
        </button>
    </li>
);
